import { BASE_CSS } from '../styles/base';

/**
 * Owns the single container appended to the host page and its open Shadow Root. All SDK UI and
 * styles live inside the shadow boundary so host CSS can neither leak in nor be affected.
 *
 * The host element is a 0×0 fixed box with a very high stacking context; every surface inside
 * positions itself with `position: fixed`, so it escapes the 0×0 box and lays out against the
 * viewport without ever taking part in the host page's flow (zero layout shift from mounting).
 */
export class ShadowHost {
  readonly hostEl: HTMLDivElement;
  readonly root: ShadowRoot;

  constructor(doc: Document = document) {
    this.hostEl = doc.createElement('div');
    this.hostEl.setAttribute('data-clickrelease-root', '');
    const s = this.hostEl.style;
    s.position = 'fixed';
    s.top = '0';
    s.left = '0';
    s.width = '0';
    s.height = '0';
    s.margin = '0';
    s.padding = '0';
    s.border = '0';
    s.zIndex = '2147483000';

    this.root = this.hostEl.attachShadow({ mode: 'open' });
    const style = doc.createElement('style');
    style.textContent = BASE_CSS;
    this.root.append(style);

    (doc.body ?? doc.documentElement).append(this.hostEl);
  }

  mount(node: Node): void {
    this.root.append(node);
  }

  destroy(): void {
    this.hostEl.remove();
  }
}
